"use client";

import { useEffect, useMemo, useState } from "react";
import { useHistoryManager } from "../history/useHistoryManager";
import { useToken } from "../../lib/context/auth-context";
import StoreBox from "../store/template/default/store-box";
import styles from "./index.module.css";
import Carousel from "../carousel/carousel";
import { buildQueryString, extractInfoFromUrl } from "../../lib/util";
import {
  useMarketplaceProductFeeds,
  useProductPublicCategories,
  useProductSearchFilters,
} from "../../lib/services/product-service";
import { useFormatCurrency } from "../common/format-currency";
import { useProductPriceDisplay } from "../store/template/default/product/useProductPriceDisplay";
import Skeleton from "react-loading-skeleton";
import Pagination from "../common/Pagination";
import { useAppDispatch } from "../slice/hooks";
import {
  setAccessPoint,
  setAccessPointId,
  setAccessPoints,
  setOrganisation,
} from "../slice/accessOrg-slice";
import { setActiveCategory, setCategories } from "../slice/category-slice";
import { useSwitchingOrgs } from "../organisation/useSwitchingOrgs";
import { closeIcon, rightArrowIcon } from "../../assets";
import { useModalManager } from "../modal/useModalManager";

import BreadcrumbMenu from "../common/BreadcrumbMenu";
import MiniCarousel from "../carousel/miniCarousel";
import MarketPlaceCategories from "./marketplaceCategories";
import ProductFilterPanel from "./productFilterPanel";
import Drawermenu from "../store/template/default/drawer-menu";
import ProductNoMediaCard from "./product-no-media-card";
import ProductSkeletonCard from "./product-skeleton-card";
import MarketplaceProductDetailsSkeleton from "./marketplace-product-details-skeleton";
import FilterPanelSkeleton from "./filter-panel-skeleton";
import MarketplaceSort from "./marketplace-sort";
import FloatingHelper from "../store/template/default/floating-helper";
import CategoryList from "../store/template/default/search-category";
import { useMarketplaceCartCollections } from "./useMarketplaceCartCollections";

const getInitialFilterStateFromUrl = () => ({
  category: extractInfoFromUrl("category") || null,
  country: extractInfoFromUrl("country") || null,
  colors: (extractInfoFromUrl("colors") || "").split(",").filter(Boolean),
  sizes: (extractInfoFromUrl("sizes") || "")
    .split(",")
    .map((size) => size.trim())
    .filter(Boolean),
  price: {
    min: extractInfoFromUrl("minPrice")
      ? Number(extractInfoFromUrl("minPrice"))
      : null,
    max: extractInfoFromUrl("maxPrice")
      ? Number(extractInfoFromUrl("maxPrice"))
      : null,
  },
});

const mapCategoriesForMobileDropdown = (categories = []) =>
  categories.map((category) => ({
    productCategoryId: category.categoryId,
    name: category.name,
    subCategories: (category.SubCategory || []).map((subCategory) => ({
      subCategoryId: subCategory._id,
      name: subCategory.name,
    })),
  }));

const normalizeProductMedia = (media = []) =>
  media
    ?.map((m) => m)
    .filter(Boolean)
    .map((m) => ({
      mediaUrl: m.mediaUrl,
      mediaThumb: m.mediaThumb,
      mediaName: m.mediaName,
      mediaType: m.mediaType,
      mediaExt: m.mediaExt,
      mediaWidth: m.mediaWidth,
      mediaHeight: m.mediaHeight,
      mediaTime: m.mediaTime,
      _id: m._id,
    }));

const ProductPrice = ({
  product,
  accessPoint = null,
  recievedPriceOption = null,
}) => {
  const { formatCurrency } = useFormatCurrency();
  const { productPrice, getDisplayPrice } = useProductPriceDisplay(
    accessPoint,
    product,
    recievedPriceOption,
  );

  const amount =
    productPrice ?? getDisplayPrice(product) ?? product?.amount ?? 0;
  const formatted = formatCurrency(amount, product.currency);

  return (
    <h4
      className={styles.priceLine}
      style={{ fontSize: "1.9rem", fontWeight: 500 }}
    >
      {formatted}
    </h4>
  );
};

const MarketplaceSearchHeader = ({
  sauce,
  searchTerm,
  setSearchTerm,
  isMobile,
  productsCount,
  onOpenFilters,
  sortOrder,
  setSortOrder,
}) => {
  if (!searchTerm) return null;

  return (
    <div className={styles.marketPlaceHeader}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          width: "100%",
        }}
      >
        <BreadcrumbMenu sauce={sauce} setSearchTerm={setSearchTerm} />
        <div className={styles.searchHeader}>
          <h1>Search results - {searchTerm}</h1>

          {isMobile ? (
            <div className={styles.mobileActions}>
              {productsCount !== 0 && (
                <button
                  onClick={onOpenFilters}
                  className={styles.mobileFilterButton}
                >
                  Filters
                </button>
              )}
              <MarketplaceSort
                searchTerm={searchTerm}
                sortOrder={sortOrder}
                setSortOrder={setSortOrder}
                isMobile={isMobile}
              />
            </div>
          ) : (
            <MarketplaceSort
              searchTerm={searchTerm}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
              isMobile={false}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const MobileCategoryDropdown = ({
  show,
  categories,
  activeCategory,
  activeSubCategory,
  onCategoryChange,
  onSubCategoryChange,
}) => {
  if (!show) return null;

  return (
    <div className={styles.mobileCategoryWrapper}>
      <CategoryList
        categories={categories}
        activeCategory={activeCategory}
        activeSubCategory={activeSubCategory}
        handleCategoryChange={onCategoryChange}
        handleSubCategoryChange={onSubCategoryChange}
        useFloatingDropdown={true}
      />
    </div>
  );
};

const DesktopCategoryShowcase = ({
  show,
  isCategoryLoading,
  categories,
  onOpenCategoryModal,
  setSearchTerm,
}) => {
  if (!show) return null;

  return (
    <div className={styles.marketPlaceTop}>
      <div className={styles.marketPlaceCategories}>
        {isCategoryLoading && (
          <>
            <div style={{ flex: 1.5 }}>
              <Skeleton
                height="100%"
                width="100%"
                style={{ borderRadius: "7px" }}
              />
            </div>

            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} style={{ flex: 1 }}>
                <Skeleton
                  height="100%"
                  width="100%"
                  style={{ borderRadius: "7px" }}
                />
              </div>
            ))}
          </>
        )}

        {!isCategoryLoading && categories.length > 0 && (
          <>
            <div className={styles.mainCategoriesList} role="button">
              {categories.map((mainCat) => (
                <div
                  key={mainCat.categoryId}
                  className={styles.mainCategoriesItem}
                  onClick={() => onOpenCategoryModal(mainCat)}
                >
                  <span>{mainCat.name}</span> <span>{rightArrowIcon}</span>
                </div>
              ))}
            </div>

            <div
              style={{
                flex: 2,
                position: "relative",
                overflow: "hidden",
                width: "100%",
                height: "100%",
              }}
            >
              <MiniCarousel
                categories={categories}
                setSearchTerm={setSearchTerm}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const DesktopFilterPanel = ({
  show,
  isLoadingFilterData,
  filterData,
  filterProps,
}) => {
  if (!show) return null;
  if (isLoadingFilterData) return <FilterPanelSkeleton />;
  if (!filterData?.productCategoryNames?.length) return null;

  return <ProductFilterPanel {...filterProps} filterData={filterData} />;
};

const MarketplaceEmptyState = ({ show, error }) => {
  if (!show || error) return null;

  return (
    <div className={styles.marketProductEmptyState}>
      <div className={styles.marketProductEmptyArt} aria-hidden="true">
        <span className={styles.emptyProductBagHandle} />
        <span className={styles.emptyProductBag}>
          <span />
          <span />
        </span>
      </div>
      <div className={styles.marketProductEmptyCopy}>
        <h2>No products available</h2>
        <p>
          We could not find products for this selection. Try a different
          category, filter, or search term.
        </p>
      </div>
    </div>
  );
};

const ProductGrid = ({
  products,
  showSkeletons,
  searchTerm,
  isMobile,
  hookSwitching,
  isLoading,
  isFetching,
  isLoadingSearchTerm,
  error,
  onProductClick,
}) => (
  <>
    {hookSwitching && <MarketplaceProductDetailsSkeleton />}

    {!hookSwitching && (
      <div className={styles.productsContainer}>
        {showSkeletons && (
          <div className={styles.productsContainer}>
            {Array.from({ length: 20 }).map((_, idx) => (
              <ProductSkeletonCard
                key={idx}
                isSearchDesktop={Boolean(searchTerm && !isMobile)}
              />
            ))}
          </div>
        )}

        <MarketplaceEmptyState
          show={
            products.length === 0 &&
            !isLoading &&
            !isFetching &&
            !isLoadingSearchTerm
          }
          error={error}
        />

        {!isLoading &&
          products.map((product) => {
            const hasMedia =
              Array.isArray(product.media) && product.media.length > 0;
            const media = normalizeProductMedia(product.media);

            return (
              <div
                key={product.productId}
                data-product-id={product.productId}
                onClick={() => onProductClick(product)}
                className={`${styles.productItem} ${
                  searchTerm && !isMobile ? styles.productItemSearch : ""
                }`}
                tabIndex={0}
                role="button"
              >
                {hasMedia ? (
                  <div>
                    <Carousel
                      media={media}
                      enablePreview={false}
                      showIndicator={false}
                      customHeight={"20rem"}
                      galleryWidth={325}
                      hideThumb={true}
                      autoscroll={true}
                      inMainMarketPlace={true}
                      galleryContainerClassName="market-place-gallery"
                    />
                  </div>
                ) : (
                  <ProductNoMediaCard
                    product={product}
                    searchTerm={searchTerm}
                    isMobile={isMobile}
                  />
                )}

                <div className={styles.itemInfo}>
                  <h4>
                    <p className={styles.names}>{product.name}</p>
                  </h4>

                  <ProductPrice
                    product={product}
                    accessPoint={null}
                    recievedPriceOption={product?.storeFrontDisplayPrice}
                  />

                  <h4>
                    <span>Sold by</span>{" "}
                    <p className={styles.names}>{product.host.name}</p>
                  </h4>
                </div>
              </div>
            );
          })}
      </div>
    )}
  </>
);

const MarketplaceFilterDrawer = ({
  show,
  setShowFilterDrawer,
  filterProps,
  filterData,
  searchTerm,
}) => {
  if (!show) return null;

  return (
    <Drawermenu showMenu={show} setShowMenu={setShowFilterDrawer}>
      {({ closeMenu }) => (
        <div className={styles.marketplaceDrawerCase}>
          <div className={styles.marketplaceDrawerHeader}>
            <h3>Search Filters</h3>
            <span className={styles.closeButton} onClick={closeMenu}>
              {closeIcon}
            </span>
          </div>

          <div className={styles.marketplaceDrawerContent}>
            <ProductFilterPanel
              {...filterProps}
              filterData={filterData}
              searchTerm={searchTerm}
              onFilterAction={closeMenu}
            />
          </div>
        </div>
      )}
    </Drawermenu>
  );
};

const MarketPlace = (props) => {
  const { storePath, searchTerm, setSearchTerm, urlData } = props;
  const [sauce, setSauce] = useState("home");
  const [error, setError] = useState(null);
  const [hasTyped, setHasTyped] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [isLoadingSearchTerm, setIsLoadingSearchTerm] = useState(false);
  const [mobileActiveCategoryId, setMobileActiveCategoryId] = useState(null);
  const [mobileActiveSubCategoryId, setMobileActiveSubCategoryId] =
    useState(null);
  const [isShowingFloater, setIsShowingFloater] = useState(false);
  const [currency, setCurrency] = useState("NGN");

  const [prevSearchTerm, setPrevSearchTerm] = useState(null);

  const [selectedFilterColors, setSelectedFilterColors] = useState([]);
  const [selectedFilterCategory, setSelectedFilterCategory] = useState(null);
  const [selectedFilterCountry, setSelectedFilterCountry] = useState(null);
  const [selectedFilterSizes, setSelectedFilterSizes] = useState([]);
  const [selectedFilterPrice, setSelectedFilterPrice] = useState({
    min: null,
    max: null,
  });

  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [sortOrder, setSortOrder] = useState("relevance");

  const dispatch = useAppDispatch();

  const { token } = useToken();
  const { navigateTo } = useHistoryManager(storePath);
  useMarketplaceCartCollections();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const initialFilters = getInitialFilterStateFromUrl();
    if (initialFilters.category)
      setSelectedFilterCategory(initialFilters.category);
    if (initialFilters.country)
      setSelectedFilterCountry(initialFilters.country);
    setSelectedFilterColors(initialFilters.colors);
    setSelectedFilterSizes(initialFilters.sizes);
    if (initialFilters.price.min || initialFilters.price.max) {
      setSelectedFilterPrice(initialFilters.price);
    }
  }, []);

  const [state, setState] = useState({
    products: [],
    limit: 20,
    total: 0,
    totalPages: 0,
    currentPage: 1,
    page: 1,
    randomSeed: null,
    search: "",
  });

  const queryKey = useMemo(
    () => [
      "feeds",
      state.limit,
      state.page,
      query,
      currency,
      selectedFilterColors,
      selectedFilterPrice?.min,
      selectedFilterPrice?.max,
      selectedFilterSizes,
      selectedFilterCategory,
      selectedFilterCountry,
      sortOrder,
      state.page > 1 ? state.randomSeed : null,
    ],
    [
      state.limit,
      state.page,
      query,
      currency,
      selectedFilterColors,
      selectedFilterPrice?.min,
      selectedFilterPrice?.max,
      selectedFilterSizes,
      selectedFilterCategory,
      selectedFilterCountry,
      sortOrder,
      state.page > 1 ? state.randomSeed : null,
    ],
  );
  const {
    data: feeds,
    isLoading,
    isFetching,
    refetch,
    isRefetching,
  } = useMarketplaceProductFeeds({
    queryKey,
    query,
    currency,
    limit: state.limit,
    page: state.page,
    randomSeed: state.randomSeed,
    selectedFilterColors,
    selectedFilterSizes,
    selectedFilterPrice,
    selectedFilterCategory,
    selectedFilterCountry,
    sortOrder,
    token,
    onError: (err) => {
      setError(err.message);
      console.error("Failed to fetch products:", err);
    },
  });

  const { data: _categories = [], isLoading: isCategoryLoading } =
    useProductPublicCategories({ token });

  const mobileDropdownCategories = useMemo(
    () => mapCategoriesForMobileDropdown(_categories?.categories || []),
    [_categories?.categories],
  );

  /* ---------------- FETCH FILTER OPTIONS ---------------- */

  const queryKeyFilter = useMemo(
    () => ["filter", searchTerm, currency],
    [searchTerm, currency],
  );
  const { data: filterData, isLoading: isLoadingFilterData } =
    useProductSearchFilters({
      queryKey: queryKeyFilter,
      currency,
      searchTerm,
      token,
    });

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 750);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    dispatch(setAccessPointId(null));
    dispatch(setAccessPoint(null));
    dispatch(setAccessPoints([]));
    dispatch(setOrganisation(null));
    dispatch(setActiveCategory(null));
    dispatch(setCategories(null));
  }, []);

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      products: [],
      page: 1,
      randomSeed: null,
    }));
  }, [
    selectedFilterCategory,
    selectedFilterColors,
    selectedFilterSizes,
    selectedFilterCountry,
    selectedFilterPrice?.min,
    selectedFilterPrice?.max,
    sortOrder,
    query,
  ]);

  useEffect(() => {
    const q = extractInfoFromUrl("q");
    if (!q) return;
    setSearchTerm(q);
  }, []);

  useEffect(() => {
    if (searchTerm.trim() !== "") {
      setHasTyped(true);
    }

    const delayDebounce = setTimeout(() => {
      activateSearch();
    }, 1000); // wait 300ms after typing stops

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  const resetPage = () => {
    // if (resetCategory) dispatch(setActiveCategory(""));
    // if (persistQuery) storeInState();
    setState((prev) => ({
      ...prev,
      products: [],
      total: 0,
      totalPages: 0,
      currentPage: 1,
      page: 1,
      randomSeed: null,
    }));
  };

  const activateSearch = (value = null) => {
    const rawValue = value !== null ? value : searchTerm || "";
    const trimmed = rawValue.trim();
    if (query.trim() == trimmed) return;

    // Reset pagination state before searching
    resetPage(false, false);

    setIsLoadingSearchTerm(true);

    // Store the active query
    setQuery(trimmed);

    // const urlQuery = buildQueryString({ q: trimmed, currency });
    const urlQuery = buildQueryString({ q: trimmed });
    navigateTo(`${window.location.pathname}${urlQuery}`, { replace: true });
  };

  useEffect(() => {
    const q = extractInfoFromUrl("q");
    if (!q) return;
    setSearchTerm(q);
  }, []);

  useEffect(() => {
    if (feeds && feeds.products) {
      const { products, limit, total, totalPages, currentPage, randomSeed } =
        feeds;
      setState((prev) => {
        const all = [...(prev.products || []), ...products];
        const unique = Array.from(
          new Map(all.map((p) => [p.productId, p])).values(),
        );
        return {
          ...prev,
          products: unique,
          limit,
          total,
          totalPages,
          currentPage,
          randomSeed: prev.page === 1 ? (randomSeed ?? null) : prev.randomSeed,
        };
      });
      setIsLoadingSearchTerm(false);
    }
  }, [feeds]);

  useEffect(() => {
    if (searchTerm && searchTerm.trim() !== "") {
      setSauce("search");
    } else {
      setSauce("home");
    }
  }, [searchTerm]);

  const openPage = (page) => {
    setState((prev) => ({ ...prev, page }));
  };

  const {
    startSwitch,
    isSwitching: hookSwitching,
    error: switchError,
  } = useSwitchingOrgs({ token });

  // const hookSwitching = true;

  const handleProductClick = (product) => {
    setSearchTerm("");
    startSwitch(product);
  };
  const handleOpenCategoryModal = (category) => {
    setSelectedCategoryId(category.categoryId);
    openModal("categories");
  };

  const { modal, openModal, closeModal, modalState, modalBackButton } =
    useModalManager();
  useEffect(() => {
    if (prevSearchTerm !== null && prevSearchTerm !== "" && searchTerm === "") {
      // User cleared search

      setSelectedFilterColors([]);
      setSelectedFilterSizes([]);
      setSelectedFilterCategory(null);
      setSelectedFilterCountry(null);
      setSelectedFilterPrice({ min: null, max: null });

      const urlQuery = buildQueryString({
        colors: null,
        sizes: null,
        category: null,
        country: null,
        minPrice: null,
        maxPrice: null,
        q: null,
      });

      navigateTo(`${window.location.pathname}${urlQuery}`, {
        replace: true,
      });

      setState((prev) => ({
        ...prev,
        products: [],
        page: 1,
        randomSeed: null,
      }));
    }

    setPrevSearchTerm(searchTerm);
  }, [searchTerm]);

  const filterProps = {
    setSelectedFilterColors,
    setSelectedFilterPrice,
    setSelectedFilterSizes,
    setSelectedFilterCategory,
    setSelectedFilterCountry,
    selectedFilterCategory,
    selectedFilterCountry,
    selectedFilterColors,
    selectedFilterSizes,
    selectedFilterPrice,
  };

  const showProductSkeletons =
    (isLoading || isFetching || isLoadingSearchTerm) &&
    state.products.length === 0;

  return (
    <StoreBox
      {...props}
      isSwitching={false}
      hasTyped={hasTyped}
      setHasTyped={setHasTyped}
      inMainMarketplace={true}
    >
      <>
        {!hookSwitching && (
          <>
            <MarketplaceSearchHeader
              sauce={sauce}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              isMobile={isMobile}
              productsCount={state.products.length}
              onOpenFilters={() => setShowFilterDrawer(true)}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
            />

            <MobileCategoryDropdown
              show={
                isMobile && !searchTerm && _categories?.categories?.length > 0
              }
              categories={mobileDropdownCategories}
              activeCategory={mobileActiveCategoryId}
              activeSubCategory={mobileActiveSubCategoryId}
              onCategoryChange={(categoryId) => {
                setMobileActiveCategoryId(categoryId);
                setMobileActiveSubCategoryId(null);
              }}
              onSubCategoryChange={({ categoryId, subCategoryId, name }) => {
                setMobileActiveCategoryId(categoryId);
                setMobileActiveSubCategoryId(subCategoryId);
                setSearchTerm(name);
              }}
            />

            <DesktopCategoryShowcase
              show={
                !searchTerm &&
                !isMobile &&
                (isCategoryLoading || _categories?.categories?.length > 0)
              }
              isCategoryLoading={isCategoryLoading}
              categories={_categories?.categories || []}
              onOpenCategoryModal={handleOpenCategoryModal}
              setSearchTerm={setSearchTerm}
            />
          </>
        )}

        <div
          className={styles.marketPlaceContainer}
          style={{
            display: searchTerm ? "flex" : "block",
            gap: searchTerm ? "2rem" : 0,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
              alignItems: "flex-start",
              maxWidth: "165rem",
              margin: "0 auto",
              gap: "2rem",
            }}
          >
            <DesktopFilterPanel
              show={Boolean(searchTerm && !isMobile)}
              isLoadingFilterData={isLoadingFilterData}
              filterData={filterData}
              filterProps={filterProps}
            />

            <ProductGrid
              products={state.products}
              showSkeletons={showProductSkeletons}
              searchTerm={searchTerm}
              isMobile={isMobile}
              hookSwitching={hookSwitching}
              isLoading={isLoading}
              isFetching={isFetching}
              isLoadingSearchTerm={isLoadingSearchTerm}
              error={error}
              onProductClick={handleProductClick}
            />
          </div>
          {!hookSwitching && (
            <Pagination
              infiniteScroll
              total={state.total}
              limit={state.limit}
              page={state.page}
              onPageClick={openPage}
              enableManualMode
              autoLoadThreshold={5}
              isLoading={isLoading}
              isFetching={isFetching}
              showEndOfList
              endOfListText="🎉 You've reached the end!"
            />
          )}
          <FloatingHelper
            modal={modal}
            setIsShowingFloater={setIsShowingFloater}
          />
        </div>

        {modal && modalState === "categories" && (
          <MarketPlaceCategories
            closeModal={closeModal}
            modalBackButton={modalBackButton}
            categories={_categories.categories}
            setSearchTerm={setSearchTerm}
            initialActiveCategoryId={selectedCategoryId}
          />
        )}

        <MarketplaceFilterDrawer
          show={
            isMobile &&
            filterData?.productCategoryNames?.length !== 0 &&
            showFilterDrawer
          }
          setShowFilterDrawer={setShowFilterDrawer}
          filterProps={filterProps}
          filterData={filterData}
          searchTerm={searchTerm}
        />
      </>
    </StoreBox>
  );
};

export default MarketPlace;
